import type { UserRole } from '@/types';
import { getDynamicAllowedEmails } from '@/services/userRegistry';
import { PROVISIONED_USERS_CHANGED_EVENT } from '@/utils/constants';

export const ALLOWED_EMAILS = [
  'achuthan.thiruvarangan@assaabloy.com',
  'sudhanshu.shukla@assaabloy.com',
  'shakthipriyan.ramaiya@hidglobal.com',
  'rajesh.vijayaraghavan@assaabloy.com',
  'subburaj.paramasivan@assaabloy.com',
  'mrudula.damisetti@hidglobal.com',
  'srinivasan.rajagopal@hidglobal.com',
  'Sreedharan.Varadananjayan@hidglobal.com',
  'harmin.riarh@assaabloy.com',
  'martin.hirniak@assaabloy.com',
  'pannirselvam.shenbagam@assaabloy.com',
  'dhakshinamoorthy.sivakumar@hidglobal.com',
  'srinithi.saiprasath@hidglobal.com',
  'trial_temp@gmail.com',
] as const;

export const DEFAULT_LOGIN_PASSWORD = 'welcome';

const STATIC_ALLOWLIST = new Set(ALLOWED_EMAILS.map((email) => normalizeEmail(email)));

let dynamicAllowlistCache: Set<string> | null = null;

export function invalidateAllowedEmailCache(): void {
  dynamicAllowlistCache = null;
}

function getDynamicAllowlistSet(): Set<string> {
  if (!dynamicAllowlistCache) {
    dynamicAllowlistCache = new Set(getDynamicAllowedEmails());
  }
  return dynamicAllowlistCache;
}

if (typeof window !== 'undefined') {
  window.addEventListener(PROVISIONED_USERS_CHANGED_EVENT, invalidateAllowedEmailCache);
}

export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

export function isAllowedEmail(email: string): boolean {
  const normalized = normalizeEmail(email);
  if (STATIC_ALLOWLIST.has(normalized)) return true;
  return getDynamicAllowlistSet().has(normalized);
}

export function isValidPassword(password: string): boolean {
  return password.trim().toLowerCase() === DEFAULT_LOGIN_PASSWORD;
}

function titleCase(segment: string): string {
  if (!segment) return '';
  return segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase();
}

/** Parse `firstname.lastname@domain` into first / last name. */
export function parseNameFromEmail(email: string): { firstName: string; lastName: string } {
  const local = normalizeEmail(email).split('@')[0] ?? '';
  const parts = local.split('.').filter(Boolean);
  const firstName = titleCase(parts[0] ?? 'User');
  const lastName = titleCase(parts.slice(1).join(' ') || parts[0] || 'User');
  return { firstName, lastName };
}

export function resolveRoleFromSignInCount(count: number): UserRole {
  return count === 1 ? 'novice' : 'operator';
}

export function workspaceRouteForRole(role: UserRole): string {
  return role === 'operator' ? '/user/operator/workspace' : '/user/novice/workspace';
}
